import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { configureAuth } from './services/auth'
import { ErrorBoundary } from './components/ErrorBoundary'

// Log uncaught errors so we can see message in production
window.addEventListener('error', (event) => {
  console.error('[Uncaught error]', event.message, event.filename, event.lineno, event.colno, event.error);
});

// Configure Amplify before any component runs (so useAuth/getCurrentUser can run)
try {
  configureAuth();
} catch (err) {
  console.error('[configureAuth failed]', err);
  throw err;
}

// Catch "Unlisted TLDs in URLs" errors - often from browser extensions or URL parsing
// when UUIDs/exercise IDs are misinterpreted as hostnames (e.g. ws://uuid)
// Also swallow 403/ApolloError from third-party scripts (e.g. browser extensions calling
// a different API such as sfd8q2ch3k.execute-api.us-east-2 - not our app's API).
// Our app only uses REST and only reacts to 403 from our own API (api.ts).
// After a frontend deploy, browsers may still have an old main bundle cached that lazy-loads
// deleted chunk files. Reload once so they pick up the current index.html + assets.
function isStaleFrontendBundleError(message: string): boolean {
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
}

const STALE_BUNDLE_RELOAD_KEY = 'ttx-stale-bundle-reload';

function reloadOnceForStaleBundle(reason: string): boolean {
  if (sessionStorage.getItem(STALE_BUNDLE_RELOAD_KEY)) return false;
  sessionStorage.setItem(STALE_BUNDLE_RELOAD_KEY, '1');
  console.warn('[Frontend deploy] Reloading to fetch latest assets:', reason);
  window.location.reload();
  return true;
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadOnceForStaleBundle('vite:preloadError');
});

window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message ?? String(event.reason ?? '');
  if (isStaleFrontendBundleError(msg) && reloadOnceForStaleBundle(msg)) {
    event.preventDefault();
    return;
  }
  const isThirdParty403 =
    (msg.includes('403') || msg.includes('ApolloError')) && msg.includes('Response not successful');
  const isUnlistedTld =
    msg.includes('Unlisted TLDs') || msg.includes('TLDs in URLs');

  if (isUnlistedTld) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  if (isThirdParty403) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  console.error('[Unhandled rejection]', msg, event.reason);
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
